import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Link as MuiLink,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { formatDateTime } from "../../../../../utils/functions";
import {
  ThinTextField,
  RejectModal,
  ThinTextAreaField,
} from "../../../../common/GeneralComponent";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { fetchData } from "../../../../../utils/functions";
import { useNavigate } from "react-router-dom";

const InProgressFields = ({ data, onDataUpdate }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loading = useSelector((state) => state);
  const userRole = loading?.auth?.user?.user_role_name || "";
  const permission = loading?.auth?.user?.user_permissions || [];
  const isSuperAdmin = userRole === "Super Admin";
  const implementationDetails = data?.implementationDetails || {};
  const boqProposedDetails = data?.boqProposedDetails || {};
  const criticalStages = data?.critical_stages || [];
  const featureName =
    boqProposedDetails?.Feature?.feature_name ||
    data?.feature_name ||
    "Feature";

  const procurementStatus =
    implementationDetails?.procurement_status || "Not Completed";
  const reasonOfProcurement =
    implementationDetails?.reason_of_procurement || "N/A";

  const mouStatus = implementationDetails?.status_of_mou || "Not Signed";
  const signedMouImage = data?.signedMouImage || null;

  const materialsStatus =
    implementationDetails?.status_of_materials || "Not Completed";
  const signedMaterialReceivingSheetImage =
    data?.signedMaterialReceivingSheetImage || null;
  const signedMaterialQualityReportImage =
    data?.signedMaterialQualityReportImage || null;
  const materialsNotCompletedReason =
    implementationDetails?.reason_of_material || "N/A";

  const siteStatus =
    implementationDetails?.status_of_site_selection || "Not Completed";
  const reasonOfSiteSelection =
    implementationDetails?.reason_of_site_selection || "N/A";
  const pictureSiteImage = data?.pictureSiteImage || null;
  const pictureLayoutImage = data?.pictureLayoutImage || null;

  // Completion report image for when all stages completed
  const completionReportImage = data?.completionReportImage || null;

  // Process critical stages with progress data
  const allStages = useMemo(() => {
    return criticalStages.map((stage) => ({
      id: stage.feature_category_critical_stages_id,
      name: stage.stage_name || "Unnamed Stage",
      weight: Number(stage.percentage || 0),
      status: stage.progress_status || "Not Completed",
      quality: stage.quality_of_work || "Pending",
      issues: stage.required_actions || null,
      completedAt: stage.created_at || null,
      proofOfCompletion: stage.proof_of_completion || null,
    }));
  }, [criticalStages]);

  // Calculate overall weighted progress based on COMPLETED stages only
  const progressPercent = useMemo(() => {
    if (!allStages || allStages.length === 0) return 0;
    const completedWeight = allStages
      .filter((stage) => stage.status === "Completed")
      .reduce((acc, stage) => acc + (Number(stage.weight) || 0), 0);
    return Math.round(completedWeight);
  }, [allStages]);

  // Check if all stages are completed
  const allStagesCompleted = useMemo(() => {
    return (
      allStages.length > 0 &&
      allStages.every((stage) => stage.status === "Completed")
    );
  }, [allStages]);

  const [expanded, setExpanded] = useState(false);
  const [rejectModal, setRejectModal] = useState({ isOpen: false, reason: "" });

  // Image modal states
  const [showMouModal, setShowMouModal] = useState(false);
  const [showMaterialReceivingModal, setShowMaterialReceivingModal] =
    useState(false);
  const [showMaterialQualityModal, setShowMaterialQualityModal] =
    useState(false);
  const [showSitePhotoModal, setShowSitePhotoModal] = useState(false);
  const [showSiteLayoutModal, setShowSiteLayoutModal] = useState(false);
  const [showCompletionReportModal, setShowCompletionReportModal] =
    useState(false);
  const [showProofModal, setShowProofModal] = useState(null); // For stage proofs

  const getImageUrl = (imagePath) => {
    if (!imagePath) return "";
    const cleanPath = imagePath.replace(/^undefined\//, "");
    if (cleanPath.startsWith("http://") || cleanPath.startsWith("https://")) {
      return cleanPath;
    }
    const baseUrl = process.env.REACT_APP_BASE_URL || "";
    return `${baseUrl}/${cleanPath}`;
  };

  const isDQAApproved = implementationDetails?.verified_from_dqa;
  const dqaApprovedAt = implementationDetails?.verified_from_dqa_datetime;
  const isPMApproved = implementationDetails?.verified_from_pm;
  const pmApprovedAt = implementationDetails?.verified_from_pm_datetime;
  const rejection_remarks = implementationDetails?.rejection_remarks || null;

  const handleChange = (stageId) => (event, isExpanded) => {
    setExpanded(isExpanded ? stageId : false);
  };

  const handleApprove = async () => {
    try {
      const approvalType =
        userRole === "DQA"
          ? "dqa-approve"
          : userRole === "Project Manager"
          ? "pm-approve"
          : "";
      const endpoint = `implementation-phase-details/approve/${data.boqs_proposed_implementation_id}`;
      await fetchData(
        endpoint,
        "PUT",
        { type: approvalType },
        {},
        {},
        navigate,
        null,
        dispatch
      );
      if (onDataUpdate) {
        onDataUpdate();
      }
      window.location.reload();
      toast.success(
        `Feature approved successfully by ${approvalType.toUpperCase()}`
      );
    } catch (error) {
      console.error("Error approving feature:", error);
      toast.error("Failed to approve feature");
    }
  };

  const handleReject = () => {
    setRejectModal({ isOpen: true, reason: "" });
  };

  const submitRejection = async (e) => {
    e.preventDefault();
    if (!rejectModal.reason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }
    try {
      const rejectionType =
        userRole === "DQA"
          ? "dqa-approve"
          : userRole === "Project Manager"
          ? "pm-approve"
          : "";
      const endpoint = `implementation-phase-details/reject/${data.boqs_proposed_implementation_id}`;
      await fetchData(
        endpoint,
        "PUT",
        { type: rejectionType, remarks: rejectModal.reason },
        {},
        {},
        navigate,
        null,
        dispatch
      );
      toast.success(
        `Feature rejected successfully by ${rejectionType.toUpperCase()}`
      );
      setRejectModal({ isOpen: false, reason: "" });
      if (onDataUpdate) {
        onDataUpdate();
      }
      window.location.reload();
    } catch (error) {
      console.error("Error rejecting feature:", error);
      toast.error("Failed to reject feature");
    }
  };

  const closeModal = () => {
    setRejectModal({ isOpen: false, reason: "" });
  };

  // Image modal handlers
  const handleMouClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (signedMouImage) setShowMouModal(true);
  };
  const closeMouModal = (e) => {
    e?.stopPropagation();
    setShowMouModal(false);
  };
  const handleMaterialReceivingClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (signedMaterialReceivingSheetImage) setShowMaterialReceivingModal(true);
  };
  const closeMaterialReceivingModal = (e) => {
    e?.stopPropagation();
    setShowMaterialReceivingModal(false);
  };
  const handleMaterialQualityClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (signedMaterialQualityReportImage) setShowMaterialQualityModal(true);
  };
  const closeMaterialQualityModal = (e) => {
    e?.stopPropagation();
    setShowMaterialQualityModal(false);
  };
  const handleSitePhotoClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (pictureSiteImage) setShowSitePhotoModal(true);
  };
  const closeSitePhotoModal = (e) => {
    e?.stopPropagation();
    setShowSitePhotoModal(false);
  };
  const handleSiteLayoutClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (pictureLayoutImage) setShowSiteLayoutModal(true);
  };
  const closeSiteLayoutModal = (e) => {
    e?.stopPropagation();
    setShowSiteLayoutModal(false);
  };
  const handleCompletionReportClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (completionReportImage) setShowCompletionReportModal(true);
  };
  const closeCompletionReportModal = (e) => {
    e?.stopPropagation();
    setShowCompletionReportModal(false);
  };
  const handleProofClick = useCallback((proofUrl, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (proofUrl) setShowProofModal(proofUrl);
  }, []);
  const closeProofModal = (e) => {
    e?.stopPropagation();
    setShowProofModal(null);
  };

  return (
    <div className="w-full bg-white rounded">
      {/* Header with Progress */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-4">
          {featureName} Implementation Progress
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="w-full">
            <ThinTextField
              isRequired={false}
              label="Overall Weighted Progress (Completed Stages)"
              value={`${progressPercent}%`}
              readOnly
            />
          </div>
          <div className="w-full md:col-span-2 flex items-end pb-3">
            <div className="w-full">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-medium text-gray-600">
                  Progress
                </span>
                <span className="text-sm font-bold text-primaryDark">
                  {progressPercent}%
                </span>
              </div>
              <div className="w-full rounded-full h-4 bg-primaryDark/10 overflow-hidden">
                <div
                  className="bg-primaryDark h-4 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progressPercent}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full">
        {/* Procurement Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="w-full">
            <ThinTextField
              isRequired={false}
              label="1) Current Status of Procurement Process"
              value={procurementStatus}
              disabled
            />
          </div>
          {procurementStatus === "Completed" ? (
            <>
              <div className="w-full">
                <ThinTextField
                  isRequired={false}
                  label="1.1) Status of MoU with Beneficiary/Community/VRC"
                  value={mouStatus}
                  disabled
                />
              </div>
              {mouStatus === "Signed" && signedMouImage ? (
                <div className="w-full">
                  <label className="text-sm font-[Regular] text-[#565656] p-2 mb-1 block">
                    MoU (Signed) File
                  </label>
                  <div className="border border-gray-300 rounded-md p-2 bg-gray-50 h-[42px] flex items-center">
                    <button
                      onClick={handleMouClick}
                      className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 px-2 py-1 rounded transition-all duration-200 border border-transparent hover:border-blue-200 w-full text-left min-h-0"
                    >
                      <i className="fa fa-image text-blue-500 text-sm flex-shrink-0"></i>
                      <span className="text-xs text-blue-600 hover:text-blue-800 font-medium truncate">
                        Signed MoU
                      </span>
                      <i className="fa fa-eye text-xs text-blue-400 flex-shrink-0 ml-auto"></i>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="w-full"></div>
              )}
            </>
          ) : (
            <>
              <div className="w-full md:col-span-2">
                <ThinTextAreaField
                  isRequired={false}
                  label="Reason for Not Completion"
                  value={reasonOfProcurement}
                  disabled
                />
              </div>
            </>
          )}
        </div>

        {/* Materials Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="w-full">
            <ThinTextField
              isRequired={false}
              label="1.2) Status of Materials Shifting & Labour Arrangement"
              value={materialsStatus}
              disabled
            />
          </div>
          {materialsStatus === "Completed" ? (
            <>
              {signedMaterialReceivingSheetImage && (
                <div className="w-full">
                  <label className="text-sm font-[Regular] text-[#565656] p-2 mb-1 block">
                    Material Receiving Sheet
                  </label>
                  <div className="border border-gray-300 rounded-md p-2 bg-gray-50 h-[42px] flex items-center">
                    <button
                      onClick={handleMaterialReceivingClick}
                      className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 px-2 py-1 rounded transition-all duration-200 border border-transparent hover:border-blue-200 w-full text-left min-h-0"
                    >
                      <i className="fa fa-image text-blue-500 text-sm flex-shrink-0"></i>
                      <span className="text-xs text-blue-600 hover:text-blue-800 font-medium truncate">
                        Material Receiving Sheet
                      </span>
                      <i className="fa fa-eye text-xs text-blue-400 flex-shrink-0 ml-auto"></i>
                    </button>
                  </div>
                </div>
              )}
              {signedMaterialQualityReportImage && (
                <div className="w-full">
                  <label className="text-sm font-[Regular] text-[#565656] p-2 mb-1 block">
                    Materials Quality Inspection Report
                  </label>
                  <div className="border border-gray-300 rounded-md p-2 bg-gray-50 h-[42px] flex items-center">
                    <button
                      onClick={handleMaterialQualityClick}
                      className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 px-2 py-1 rounded transition-all duration-200 border border-transparent hover:border-blue-200 w-full text-left min-h-0"
                    >
                      <i className="fa fa-image text-blue-500 text-sm flex-shrink-0"></i>
                      <span className="text-xs text-blue-600 hover:text-blue-800 font-medium truncate">
                        Quality Report
                      </span>
                      <i className="fa fa-eye text-xs text-blue-400 flex-shrink-0 ml-auto"></i>
                    </button>
                  </div>
                </div>
              )}
              {!signedMaterialReceivingSheetImage &&
                !signedMaterialQualityReportImage && (
                  <>
                    <div className="w-full"></div>
                    <div className="w-full"></div>
                  </>
                )}
              {signedMaterialReceivingSheetImage &&
                !signedMaterialQualityReportImage && (
                  <div className="w-full"></div>
                )}
              {!signedMaterialReceivingSheetImage &&
                signedMaterialQualityReportImage && (
                  <div className="w-full"></div>
                )}
            </>
          ) : materialsStatus === "Not Completed" ? (
            <>
              <div className="w-full md:col-span-2">
                <ThinTextAreaField
                  isRequired={false}
                  label="Reason for Not Completion"
                  value={materialsNotCompletedReason}
                  disabled
                />
              </div>
            </>
          ) : (
            <>
              <div className="w-full"></div>
              <div className="w-full"></div>
            </>
          )}
        </div>

        {/* Site Selection Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="w-full">
            <ThinTextField
              isRequired={false}
              label="1.3) Status of Site Selection / Layout"
              value={siteStatus}
              disabled
            />
          </div>
          {siteStatus === "Completed" ? (
            <>
              {pictureSiteImage && (
                <div className="w-full">
                  <label className="text-sm font-[Regular] text-[#565656] p-2 mb-1 block">
                    Site Photo
                  </label>
                  <div className="border border-gray-300 rounded-md p-2 bg-gray-50 h-[42px] flex items-center">
                    <button
                      onClick={handleSitePhotoClick}
                      className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 px-2 py-1 rounded transition-all duration-200 border border-transparent hover:border-blue-200 w-full text-left min-h-0"
                    >
                      <i className="fa fa-image text-blue-500 text-sm flex-shrink-0"></i>
                      <span className="text-xs text-blue-600 hover:text-blue-800 font-medium truncate">
                        Site Photo
                      </span>
                      <i className="fa fa-eye text-xs text-blue-400 flex-shrink-0 ml-auto"></i>
                    </button>
                  </div>
                </div>
              )}
              {pictureLayoutImage && (
                <div className="w-full">
                  <label className="text-sm font-[Regular] text-[#565656] p-2 mb-1 block">
                    Site Layout
                  </label>
                  <div className="border border-gray-300 rounded-md p-2 bg-gray-50 h-[42px] flex items-center">
                    <button
                      onClick={handleSiteLayoutClick}
                      className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 px-2 py-1 rounded transition-all duration-200 border border-transparent hover:border-blue-200 w-full text-left min-h-0"
                    >
                      <i className="fa fa-image text-blue-500 text-sm flex-shrink-0"></i>
                      <span className="text-xs text-blue-600 hover:text-blue-800 font-medium truncate">
                        Site Layout
                      </span>
                      <i className="fa fa-eye text-xs text-blue-400 flex-shrink-0 ml-auto"></i>
                    </button>
                  </div>
                </div>
              )}
              {!pictureSiteImage && !pictureLayoutImage && (
                <>
                  <div className="w-full"></div>
                  <div className="w-full"></div>
                </>
              )}
              {pictureSiteImage && !pictureLayoutImage && (
                <div className="w-full"></div>
              )}
              {!pictureSiteImage && pictureLayoutImage && (
                <div className="w-full"></div>
              )}
            </>
          ) : siteStatus === "Not Completed" ? (
            <>
              <div className="w-full md:col-span-2">
                <ThinTextAreaField
                  isRequired={false}
                  label="Reason for Not Completion"
                  value={reasonOfSiteSelection}
                  disabled
                />
              </div>
            </>
          ) : (
            <>
              <div className="w-full"></div>
              <div className="w-full"></div>
            </>
          )}
        </div>

        {/* Completion Report - Show when all stages completed */}
        {allStagesCompleted && completionReportImage && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="w-full">
              <label className="text-sm font-[Regular] text-[#565656] p-2 mb-1 block">
                Completion Report (MOV)
              </label>
              <div className="border border-gray-300 rounded-md p-2 bg-gray-50 h-[42px] flex items-center">
                <button
                  onClick={handleCompletionReportClick}
                  className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 px-2 py-1 rounded transition-all duration-200 border border-transparent hover:border-blue-200 w-full text-left min-h-0"
                >
                  <i className="fa fa-image text-blue-500 text-sm flex-shrink-0"></i>
                  <span className="text-xs text-blue-600 hover:text-blue-800 font-medium truncate">
                    Completion Report
                  </span>
                  <i className="fa fa-eye text-xs text-blue-400 flex-shrink-0 ml-auto"></i>
                </button>
              </div>
            </div>
            <div className="w-full"></div>
            <div className="w-full"></div>
          </div>
        )}

        {/* Critical Stages */}
        <div className="mb-4">
          <h3 className="font-semibold mb-3 text-lg">
            A) Progress Status - Critical Stages
          </h3>
          <div className="space-y-3">
            {allStages.map((stage, index) => (
              <Accordion
                key={stage.id}
                expanded={expanded === stage.id}
                onChange={handleChange(stage.id)}
                sx={{
                  borderRadius: "12px",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                  overflow: "hidden",
                  backgroundColor: "#fafafa",
                  "&:before": { display: "none" },
                  "&.Mui-expanded": {
                    margin: "8px 0",
                    backgroundColor: "#f0fdfa",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "#137e81" }} />}
                  aria-controls={`stage-content-${stage.id}`}
                  id={`stage-header-${stage.id}`}
                  sx={{
                    "& .MuiAccordionSummary-content": {
                      margin: "8px 0",
                      alignItems: "center",
                    },
                  }}
                >
                  <div className="flex flex-col">
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      color="text.primary"
                    >
                      Stage {index + 1}: {stage.name}{" "}
                      <span className="text-gray-500 text-sm font-normal">
                        ({stage.weight}%)
                      </span>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Status:{" "}
                      <span
                        className={`${
                          stage.status === "Completed"
                            ? "text-green-600"
                            : stage.status === "Pending"
                            ? "text-yellow-600"
                            : "text-gray-500"
                        } font-medium`}
                      >
                        {stage.status}
                      </span>
                    </Typography>
                  </div>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    backgroundColor: "white",
                    borderTop: "1px solid #e5e7eb",
                    padding: "16px 20px",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <ThinTextField
                        isRequired={false}
                        label="Status"
                        value={stage.status}
                        readOnly
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <ThinTextField
                        isRequired={false}
                        label="Quality"
                        value={stage.quality}
                        readOnly
                      />
                    </Grid>
                    {stage.completedAt && (
                      <Grid item xs={12} md={4}>
                        <ThinTextField
                          isRequired={false}
                          label="Completed At"
                          value={formatDateTime(stage.completedAt)}
                          readOnly
                        />
                      </Grid>
                    )}
                    {stage.issues && (
                      <Grid item xs={12} md={4}>
                        <ThinTextField
                          isRequired={false}
                          label="Issues / Required Actions"
                          value={stage.issues}
                          readOnly
                        />
                      </Grid>
                    )}
                    {stage.proofOfCompletion && (
                      <Grid item xs={12} md={4}>
                        <label className="text-sm font-[Regular] text-[#565656] p-2 mb-1 block">
                          Proof of Completion (MOV)
                        </label>
                        <div className="border border-gray-300 rounded-md p-2 bg-gray-50 h-[42px] flex items-center">
                          <button
                            onClick={(e) =>
                              handleProofClick(stage.proofOfCompletion, e)
                            }
                            className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 px-2 py-1 rounded transition-all duration-200 border border-transparent hover:border-blue-200 w-full text-left min-h-0"
                          >
                            <i className="fa fa-image text-blue-500 text-sm flex-shrink-0"></i>
                            <span className="text-xs text-blue-600 hover:text-blue-800 font-medium truncate">
                              Proof of Completion
                            </span>
                            <i className="fa fa-eye text-xs text-blue-400 flex-shrink-0 ml-auto"></i>
                          </button>
                        </div>
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </div>
        </div>
      </div>

      {/* Approval Buttons */}
      {!isSuperAdmin && (
        <>
          {userRole === "DQA" && isDQAApproved === null && (
            <div className="flex justify-end gap-3 pt-3 border-t">
              <button
                type="button"
                onClick={handleApprove}
                className="bg-teal-600 text-white rounded-lg px-4 py-2 hover:bg-teal-700 transition-colors text-sm"
              >
                Approve
              </button>
              <button
                type="button"
                onClick={handleReject}
                className="bg-red-600 text-white rounded-lg px-4 py-2 hover:bg-red-700 transition-colors text-sm"
              >
                Reject
              </button>
            </div>
          )}
          {userRole === "Project Manager" &&
            isDQAApproved === 1 &&
            isPMApproved === null && (
              <div className="flex justify-end gap-3 pt-3 border-t">
                <button
                  type="button"
                  onClick={handleApprove}
                  className="bg-teal-600 text-white rounded-lg px-4 py-2 hover:bg-teal-700 transition-colors text-sm"
                >
                  Approve
                </button>
                <button
                  type="button"
                  onClick={handleReject}
                  className="bg-red-600 text-white rounded-lg px-4 py-2 hover:bg-red-700 transition-colors text-sm"
                >
                  Reject
                </button>
              </div>
            )}
        </>
      )}

      {/* Status Display */}
      {(isDQAApproved !== null || isPMApproved !== null) && (
        <div className="flex flex-col justify-end items-end gap-2 pt-3 border-t">
          {(() => {
            let finalStatus = null;
            let decidedBy = "";
            let decidedAt = "";
            let reason = "";
            if (isPMApproved === 1 || isPMApproved === 0) {
              finalStatus = isPMApproved === 1 ? "Approved" : "Rejected";
              decidedBy = "PM";
              decidedAt = pmApprovedAt;
              reason = isPMApproved === 0 ? rejection_remarks : "";
            } else if (isDQAApproved === 1 || isDQAApproved === 0) {
              finalStatus = isDQAApproved === 1 ? "Approved" : "Rejected";
              decidedBy = "DQA";
              decidedAt = dqaApprovedAt;
              reason = isDQAApproved === 0 ? rejection_remarks : "";
            } else {
              return null;
            }
            if (isSuperAdmin) {
              if (isPMApproved === 0) {
                finalStatus = "Rejected";
                decidedBy = "PM";
                decidedAt = pmApprovedAt;
                reason = rejection_remarks;
              } else if (isPMApproved === null && isDQAApproved === 1) {
                finalStatus = "Approved";
                decidedBy = "DQA";
                decidedAt = dqaApprovedAt;
              }
            }
            return (
              <>
                <span className="text-sm font-semibold text-gray-500 italic">
                  This section has been{" "}
                  <span
                    className={
                      finalStatus === "Approved"
                        ? "text-green-600"
                        : "text-red-600"
                    }
                  >
                    {finalStatus}
                  </span>{" "}
                  by {decidedBy} on {formatDateTime(decidedAt)}.
                </span>
                {finalStatus === "Rejected" && reason && (
                  <span className="text-sm font-semibold text-gray-500 italic text-red-600">
                    REASON: {reason}
                  </span>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal.isOpen && (
        <RejectModal
          isOpen={rejectModal.isOpen}
          reason={rejectModal.reason}
          onClose={closeModal}
          onChangeReason={(val) =>
            setRejectModal((prev) => ({ ...prev, reason: val }))
          }
          onSubmit={submitRejection}
        />
      )}

      {/* Image Modals */}
      {showMouModal && signedMouImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[9999]"
          onClick={closeMouModal}
        >
          <div
            className="relative bg-white rounded-lg shadow-lg p-4 max-w-3xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeMouModal}
              className="absolute top-2 right-2 text-gray-600 hover:text-black"
            >
              ✕
            </button>
            <h2 className="text-center text-gray-800 mb-3 text-lg font-semibold">
              Signed MoU
            </h2>
            <img
              src={getImageUrl(signedMouImage)}
              alt="Signed MoU"
              className="max-h-[80vh] object-contain rounded-md border border-gray-300"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src =
                  "https://via.placeholder.com/600x400?text=Image+not+found";
              }}
            />
            <a
              href={getImageUrl(signedMouImage)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-700 text-sm underline mt-2 text-center"
            >
              View Full Image
            </a>
          </div>
        </div>
      )}
      {showMaterialReceivingModal && signedMaterialReceivingSheetImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[9999]"
          onClick={closeMaterialReceivingModal}
        >
          <div
            className="relative bg-white rounded-lg shadow-lg p-4 max-w-3xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeMaterialReceivingModal}
              className="absolute top-2 right-2 text-gray-600 hover:text-black"
            >
              ✕
            </button>
            <h2 className="text-center text-gray-800 mb-3 text-lg font-semibold">
              Material Receiving Sheet
            </h2>
            <img
              src={getImageUrl(signedMaterialReceivingSheetImage)}
              alt="Material Receiving Sheet"
              className="max-h-[80vh] object-contain rounded-md border border-gray-300"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src =
                  "https://via.placeholder.com/600x400?text=Image+not+found";
              }}
            />
            <a
              href={getImageUrl(signedMaterialReceivingSheetImage)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-700 text-sm underline mt-2 text-center"
            >
              View Full Image
            </a>
          </div>
        </div>
      )}
      {showMaterialQualityModal && signedMaterialQualityReportImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[9999]"
          onClick={closeMaterialQualityModal}
        >
          <div
            className="relative bg-white rounded-lg shadow-lg p-4 max-w-3xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeMaterialQualityModal}
              className="absolute top-2 right-2 text-gray-600 hover:text-black"
            >
              ✕
            </button>
            <h2 className="text-center text-gray-800 mb-3 text-lg font-semibold">
              Quality Report
            </h2>
            <img
              src={getImageUrl(signedMaterialQualityReportImage)}
              alt="Quality Report"
              className="max-h-[80vh] object-contain rounded-md border border-gray-300"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src =
                  "https://via.placeholder.com/600x400?text=Image+not+found";
              }}
            />
            <a
              href={getImageUrl(signedMaterialQualityReportImage)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-700 text-sm underline mt-2 text-center"
            >
              View Full Image
            </a>
          </div>
        </div>
      )}
      {showSitePhotoModal && pictureSiteImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[9999]"
          onClick={closeSitePhotoModal}
        >
          <div
            className="relative bg-white rounded-lg shadow-lg p-4 max-w-3xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeSitePhotoModal}
              className="absolute top-2 right-2 text-gray-600 hover:text-black"
            >
              ✕
            </button>
            <h2 className="text-center text-gray-800 mb-3 text-lg font-semibold">
              Site Photo
            </h2>
            <img
              src={getImageUrl(pictureSiteImage)}
              alt="Site Photo"
              className="max-h-[80vh] object-contain rounded-md border border-gray-300"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src =
                  "https://via.placeholder.com/600x400?text=Image+not+found";
              }}
            />
            <a
              href={getImageUrl(pictureSiteImage)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-700 text-sm underline mt-2 text-center"
            >
              View Full Image
            </a>
          </div>
        </div>
      )}
      {showSiteLayoutModal && pictureLayoutImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[9999]"
          onClick={closeSiteLayoutModal}
        >
          <div
            className="relative bg-white rounded-lg shadow-lg p-4 max-w-3xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeSiteLayoutModal}
              className="absolute top-2 right-2 text-gray-600 hover:text-black"
            >
              ✕
            </button>
            <h2 className="text-center text-gray-800 mb-3 text-lg font-semibold">
              Site Layout
            </h2>
            <img
              src={getImageUrl(pictureLayoutImage)}
              alt="Site Layout"
              className="max-h-[80vh] object-contain rounded-md border border-gray-300"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src =
                  "https://via.placeholder.com/600x400?text=Image+not+found";
              }}
            />
            <a
              href={getImageUrl(pictureLayoutImage)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-700 text-sm underline mt-2 text-center"
            >
              View Full Image
            </a>
          </div>
        </div>
      )}
      {showCompletionReportModal && completionReportImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[9999]"
          onClick={closeCompletionReportModal}
        >
          <div
            className="relative bg-white rounded-lg shadow-lg p-4 max-w-3xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeCompletionReportModal}
              className="absolute top-2 right-2 text-gray-600 hover:text-black"
            >
              ✕
            </button>
            <h2 className="text-center text-gray-800 mb-3 text-lg font-semibold">
              Completion Report
            </h2>
            <img
              src={getImageUrl(completionReportImage)}
              alt="Completion Report"
              className="max-h-[80vh] object-contain rounded-md border border-gray-300"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src =
                  "https://via.placeholder.com/600x400?text=Image+not+found";
              }}
            />
            <a
              href={getImageUrl(completionReportImage)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-700 text-sm underline mt-2 text-center"
            >
              View Full Image
            </a>
          </div>
        </div>
      )}
      {showProofModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[9999]"
          onClick={closeProofModal}
        >
          <div
            className="relative bg-white rounded-lg shadow-lg p-4 max-w-3xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeProofModal}
              className="absolute top-2 right-2 text-gray-600 hover:text-black"
            >
              ✕
            </button>
            <h2 className="text-center text-gray-800 mb-3 text-lg font-semibold">
              Proof of Completion
            </h2>
            <img
              src={getImageUrl(showProofModal)}
              alt="Proof of Completion"
              className="max-h-[80vh] object-contain rounded-md border border-gray-300"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src =
                  "https://via.placeholder.com/600x400?text=Image+not+found";
              }}
            />
            <a
              href={getImageUrl(showProofModal)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-700 text-sm underline mt-2 text-center"
            >
              View Full Image
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default InProgressFields;
